import { useRecoilValue } from 'recoil';
import { useTranslation } from '@zurich/common/hooks';
import { Button, Stack, Typography } from '@zurich/ui/atoms';
import { styled } from '@zurich/ui/legacy/utils';
import type { StepNames } from '~/lib/types';
import { damagesFieldSelector } from '~/state';
import { DrawerDamage } from './DrawerDamage';

type DocumentDamageAllPhotosProps = {
  toggleDrawer: (toggle: boolean) => () => void;
  handleShowDamageContent: () => void;
  stepName: StepNames;
};

const maxNumberOfPhotos = 3;

export function DocumentDamageAllPhotos({
  toggleDrawer,
  handleShowDamageContent,
  stepName,
}: DocumentDamageAllPhotosProps) {
  const { t } = useTranslation();

  const damages = useRecoilValue(damagesFieldSelector);
  const stepDamages = damages.filter((damage) => damage.area === stepName);

  const showAddButton = stepDamages.length < maxNumberOfPhotos;

  return (
    <ContainerStyled>
      <Stack rowGap={1} alignItems="center">
        <StackStyled flexDirection="row" rowGap={3} width="100%">
          <ButtonStyled
            onClick={toggleDrawer(false)}
            icon="arrow-left-1"
            variant="text"
            dataName="damage-overview-arrow-button"
          />
          {stepDamages.length === 0 && (
            <Typography variant="h5" color="darkBlue">
              {t('vsga:damagePhoto.noPhotos')}
            </Typography>
          )}
        </StackStyled>
        {stepDamages.map((damage, i) => (
          <div key={damage.id} style={{ width: '100%' }}>
            {damage.typeOfDamage &&
              damage.areaOfDamage &&
              damage.descriptionOfDamage && (
                <DrawerDamage
                  index={i}
                  damageId={damage.id}
                  typeOfDamage={damage.typeOfDamage}
                  areaOfDamage={damage.areaOfDamage}
                  lastDamage={i === stepDamages.length - 1}
                  imageURL={damage.imageURL}
                />
              )}
          </div>
        ))}
      </Stack>
      {showAddButton && (
        <StackStyled>
          <Button
            onClick={handleShowDamageContent}
            icon="plus"
            variant="contained"
          >
            {t('vsga:general.addDamage')}
          </Button>
        </StackStyled>
      )}
    </ContainerStyled>
  );
}

const ButtonStyled = styled(Button)(({ theme }) => ({
  paddingLeft: `${theme.spacing(0)} !important`,
}));

const StackStyled = styled(Stack)(() => ({
  alignItems: 'center',
  paddingTop: '16px',
  paddingBottom: '24px',
}));

const ContainerStyled = styled(Stack)(({ theme }) => ({
  rowGap: theme.spacing(1),
  justifyContent: 'space-between',
  height: '100%',
  paddingBottom: '24px',
}));
